import { listEquipmentQueryOptions } from "@packages/api-client/hooks/equipmentController";
import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDeleteEquipment } from "@/api/equipment";
import { OfflineActionGuard } from "@/components/OfflineActionGuard";
import { OfflineUnavailable } from "@/components/OfflineUnavailable";
import { Button } from "@/components/ui/button";
import { useEquipmentListData } from "@/hooks/useEquipmentListData";
import { isAllFarms, useFarmScope } from "@/hooks/useFarmScope";
import { useOfflineGuard } from "@/hooks/useOfflineGuard";
import { EquipmentList } from "./-components/EquipmentList";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/equipment/fleet/",
)({
	// Fleet list works offline from the prepared equipment dataset (roadmap
	// step 5): the read side of breakdown reporting in the field.
	staticData: { offline: "ready" },
	loader: ({ context, params }) => {
		// Fire-and-forget prefetch: if cached (online or from IDB), component sees
		// data immediately. If not cached offline, the prepared dataset (or the
		// preparable panel) takes over.
		context.queryClient.ensureQueryData({
			...listEquipmentQueryOptions(params.orgId),
			staleTime: Number.POSITIVE_INFINITY,
		});
	},
	component: FleetListPage,
});

function FleetListPage() {
	const { orgId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();
	const scope = useFarmScope();
	// Fleet CRUD (create/delete) is online-only; the offline fleet list is a
	// read-only reference (roadmap Tier 2).
	const { online, blocked } = useOfflineGuard();

	const { rows, isLoading, isPrepared } = useEquipmentListData({
		orgId,
		scope,
	});

	const deleteMutation = useDeleteEquipment();

	const handleDelete = (item: { id: string }) => {
		if (window.confirm(t("equipment.fleet.confirmDelete"))) {
			deleteMutation.mutate({
				organizationId: orgId,
				equipmentId: item.id,
			});
		}
	};

	// When offline and not prepared: clear unavailable state (no hang/error).
	if (!online && !isPrepared) {
		return <OfflineUnavailable variant="preparable" />;
	}

	return (
		<div className="flex flex-col gap-4 h-full">
			<div className="flex justify-end">
				<OfflineActionGuard blocked={blocked}>
					<Button
						aria-disabled={blocked}
						onClick={() =>
							navigate({
								to: "/app/org/$orgId/equipment/fleet/new",
								params: { orgId },
							})
						}
					>
						<Plus className="h-4 w-4 mr-2" />
						{t("equipment.fleet.create.title")}
					</Button>
				</OfflineActionGuard>
			</div>

			<div className="card bg-base-100 shadow-md flex-1">
				<div className="card-body">
					<EquipmentList
						equipment={rows}
						orgId={orgId}
						onDelete={handleDelete}
						isLoading={isLoading}
						canManage={online}
						showLocation={isAllFarms(scope)}
					/>
				</div>
			</div>
		</div>
	);
}
